import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  buildPanelChangeGuides,
  computeGuideStripLayouts,
  decoratePanelChangeGuides,
} from '../helpers/panelChangeGuides';

/**
 * Encapsulates chart guide markers for:
 * - analytics change-log (`panelChanges`)
 * - prebid SSP Direct/Reseller flips (`sspTypeChanges`)
 *
 * Builds guide lines, measures clickable/hover badge strips, and (analytics only)
 * opens the detail panel. Returns state, refs and AmCharts event handlers.
 */
const usePanelChangeGuides = ({type, panelChanges, sspTypeChanges = null, theme, period, data}) => {
  const graphChartContainerRef = useRef(null);
  const primaryAnalyticsChartRef = useRef(null);
  // Geometry signature of the last successful strip layout; lets us skip the expensive
  // DOM-measuring recompute on redraws that don't change geometry (e.g. hover).
  const panelChangeLayoutSigRef = useRef(null);

  const [panelChangeDetail, setPanelChangeDetail] = useState(null);
  const [guideStripLayouts, setGuideStripLayouts] = useState([]);

  const guidesEnabled = type === 'analytics' || type === 'prebid';
  // Prebid SSP flips share guide geometry helpers but not analytics setting-key counting.
  // Pass countsByDate only so strip badges use the API counts; tooltip still reads changesByDate.
  const guideSource = useMemo(() => {
    if (type === 'prebid') {
      return sspTypeChanges?.countsByDate ? {countsByDate: sspTypeChanges.countsByDate} : null;
    }
    return panelChanges;
  }, [type, sspTypeChanges, panelChanges]);

  const panelChangeGuides = useMemo(
    () => (guidesEnabled && guideSource ? buildPanelChangeGuides(guideSource, theme) : []),
    [guidesEnabled, guideSource, theme]
  );

  useEffect(() => {
    panelChangeLayoutSigRef.current = null;
    setPanelChangeDetail(null);
    setGuideStripLayouts([]);
  }, [guideSource, period, data]);

  const openPanelChangeDetail = useCallback((date, anchorRect) => {
    setPanelChangeDetail({date, anchorRect});
  }, []);

  const guideStripLayoutsEqual = useCallback((current, next) => {
    if (current.length !== next.length) return false;
    return current.every((strip, index) => {
      const other = next[index];
      return (
        strip.date === other.date &&
        strip.count === other.count &&
        strip.left === other.left &&
        strip.top === other.top &&
        strip.width === other.width &&
        strip.height === other.height
      );
    });
  }, []);

  const updateGuideStripLayouts = useCallback(
    chart => {
      if (!guidesEnabled || !guideSource?.countsByDate || !chart) {
        panelChangeLayoutSigRef.current = null;
        setGuideStripLayouts(prev => (prev.length === 0 ? prev : []));
        return;
      }

      const containerEl = graphChartContainerRef.current;
      if (!containerEl) return;

      // The marker geometry only changes with the data, the zoom range, or the chart/container
      // size. Redraws triggered by hover (which re-validate the chart) leave geometry untouched,
      // so skip the costly SVG scan + getBoundingClientRect storm when the signature is unchanged.
      const axis = chart.categoryAxis;
      const zoomKey =
        axis?.startIndex != null && axis?.endIndex != null
          ? `${axis.startIndex}-${axis.endIndex}`
          : 'all';
      const containerRect = containerEl.getBoundingClientRect();
      const signature = [
        Object.keys(guideSource.countsByDate).length,
        zoomKey,
        Math.round(chart.plotAreaWidth || 0),
        Math.round(chart.plotAreaHeight || 0),
        Math.round(containerRect.width),
        Math.round(containerRect.height),
      ].join('|');

      // We only store the signature once a non-empty layout is produced, so retries keep
      // running while the chart geometry isn't ready yet.
      if (panelChangeLayoutSigRef.current === signature) return;

      const layouts = computeGuideStripLayouts(chart, guideSource, containerEl);
      if (layouts.length > 0) {
        panelChangeLayoutSigRef.current = signature;
      }
      setGuideStripLayouts(prev => (guideStripLayoutsEqual(prev, layouts) ? prev : layouts));
    },
    [guidesEnabled, guideSource, guideStripLayoutsEqual]
  );

  const handlePrimaryAnalyticsChartInit = useCallback(e => {
    primaryAnalyticsChartRef.current = e.chart;
    panelChangeLayoutSigRef.current = null;
    if (e.chart) {
      e.chart._panelChangeGuideSignature = null;
    }
  }, []);

  const handlePanelChangeGuidesDrawn = useCallback(
    e => {
      if (!guidesEnabled || !guideSource?.countsByDate) return;
      decoratePanelChangeGuides(e.chart, guideSource, {theme});
      // Recompute marker geometry from the freshly rendered guide nodes.
      // Guarded by equality so a stable layout won't trigger a render loop.
      updateGuideStripLayouts(e.chart);
    },
    [guidesEnabled, guideSource, theme, updateGuideStripLayouts]
  );

  const handlePanelChangeZoomed = useCallback(
    e => {
      if (!guidesEnabled || !guideSource?.countsByDate) return;
      e.chart._panelChangeGuideSignature = null;
      decoratePanelChangeGuides(e.chart, guideSource, {theme});
      updateGuideStripLayouts(e.chart);
    },
    [guidesEnabled, guideSource, theme, updateGuideStripLayouts]
  );

  useEffect(() => {
    if (!primaryAnalyticsChartRef.current) return undefined;
    primaryAnalyticsChartRef.current._panelChangeGuideSignature = null;

    if (!guideSource?.countsByDate) return undefined;

    const chart = primaryAnalyticsChartRef.current;
    const timeoutId = window.setTimeout(() => {
      decoratePanelChangeGuides(chart, guideSource, {theme});
      updateGuideStripLayouts(chart);
    }, 100);

    const retryId = window.setTimeout(() => {
      updateGuideStripLayouts(chart);
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
      window.clearTimeout(retryId);
    };
  }, [guideSource, theme, updateGuideStripLayouts]);

  // While the change-log panel is open, actively dismiss the chart cursor/balloon. The wrapper's
  // pointerEvents:'none' stops new hovers, but it can't clear a balloon that was already showing
  // when the panel opened, so we hide it imperatively here.
  useEffect(() => {
    const chart = primaryAnalyticsChartRef.current;
    const cursor = chart?.chartCursor;
    if (!cursor) return undefined;

    if (panelChangeDetail) {
      cursor.hideCursor?.();
      return undefined;
    }

    cursor.showCursor?.();
    return undefined;
  }, [panelChangeDetail]);

  useEffect(() => {
    if (!guidesEnabled || !guideSource?.countsByDate) return undefined;

    const onResize = () => {
      if (primaryAnalyticsChartRef.current) {
        updateGuideStripLayouts(primaryAnalyticsChartRef.current);
      }
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [guidesEnabled, guideSource, updateGuideStripLayouts]);

  return {
    graphChartContainerRef,
    panelChangeGuides,
    guideStripLayouts,
    panelChangeDetail,
    setPanelChangeDetail,
    openPanelChangeDetail,
    handlePrimaryAnalyticsChartInit,
    handlePanelChangeGuidesDrawn,
    handlePanelChangeZoomed,
  };
};

export default usePanelChangeGuides;
